import { useState, useEffect, useRef } from 'react'
import api from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'
import {
  Bell,
  MessageSquare,
  Mail,
  Send,
  CheckCheck,
  Check,
  ChevronDown,
  ChevronUp,
  X,
  Info,
  AlertCircle,
  AlertTriangle,
  Megaphone,
  Reply,
  Inbox,
  Search
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

const TYPE_ICON = {
  info: Info,
  warning: AlertTriangle,
  alert: AlertCircle,
  success: Check,
  announcement: Megaphone
}
const TYPE_COLOR = {
  info:         'bg-blue-500/10 text-blue-400 border-blue-500/25',
  warning:      'bg-yellow-500/10 text-yellow-400 border-yellow-500/25',
  alert:        'bg-red-500/10 text-red-400 border-red-500/25',
  success:      'bg-green-500/10 text-green-400 border-green-500/25',
  announcement: 'bg-purple-500/10 text-purple-400 border-purple-500/25',
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000)
  if (diff < 60) return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function Messages() {
  const { user } = useAuthStore()
  const [tab, setTab] = useState('notifications')
  const [notifications, setNotifications] = useState([])
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(null)

  // Compose state
  const [composing, setComposing] = useState(false)
  const [replyTo, setReplyTo] = useState(null)
  const [composeData, setComposeData] = useState({ recipient_id: '', subject: '', message: '' })
  const [sending, setSending] = useState(false)

  // Staff list for compose
  const [staff, setStaff] = useState([])

  // Message filtering
  const [msgFilter, setMsgFilter] = useState('all')
  const [msgSearch, setMsgSearch] = useState('')

  useEffect(() => {
    fetchAll()
    fetchStaff()
  }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [notifRes, msgRes] = await Promise.all([
        api.get('/communication/notifications'),
        api.get('/communication/messages')
      ])
      setNotifications(notifRes.data)
      setMessages(msgRes.data)
    } catch {
      toast.error('Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  const fetchStaff = async () => {
    try {
      // Get trainers for composing messages
      const res = await api.get('/students/my-trainers')
      setStaff(res.data || [])
    } catch {
      // Not critical
    }
  }

  const markNotifRead = async (id) => {
    try {
      await api.put(`/communication/notifications/${id}/read`)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
    } catch {}
  }

  const markAllNotifsRead = async () => {
    try {
      await api.put('/communication/notifications/mark-all-read')
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      toast.success('All notifications marked as read')
    } catch {
      toast.error('Failed to mark as read')
    }
  }

  const markMsgRead = async (id) => {
    try {
      await api.put(`/communication/messages/${id}/read`)
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m))
    } catch {}
  }

  const toggleExpand = (id, type) => {
    const key = `${type}-${id}`
    if (expanded === key) {
      setExpanded(null)
    } else {
      setExpanded(key)
      if (type === 'notif') markNotifRead(id)
      if (type === 'msg') markMsgRead(id)
    }
  }

  const handleReply = (msg) => {
    setReplyTo(msg)
    setComposeData({
      recipient_id: msg.sender_id === user.id ? msg.recipient_id : msg.sender_id,
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      message: ''
    })
    setComposing(true)
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (!composeData.recipient_id || !composeData.message.trim()) {
      toast.error('Please fill in recipient and message')
      return
    }
    setSending(true)
    try {
      await api.post('/communication/messages', {
        recipient_id: parseInt(composeData.recipient_id),
        subject: composeData.subject || '(no subject)',
        message: composeData.message,
        parent_message_id: replyTo?.id || null
      })
      toast.success('Message sent!')
      setComposing(false)
      setReplyTo(null)
      setComposeData({ recipient_id: '', subject: '', message: '' })
      fetchAll()
    } catch {
      toast.error('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  const unreadNotifs = notifications.filter(n => !n.is_read).length
  const unreadMsgs = messages.filter(m => m.recipient_id === user?.id && !m.is_read).length

  const filteredMessages = messages.filter(msg => {
    const isReceived = msg.recipient_id === user?.id
    const otherName = isReceived
      ? `${msg.sender_first_name} ${msg.sender_last_name}`
      : `${msg.recipient_first_name} ${msg.recipient_last_name}`
    const matchesSearch = msgSearch === '' ||
      otherName.toLowerCase().includes(msgSearch.toLowerCase()) ||
      (msg.subject || '').toLowerCase().includes(msgSearch.toLowerCase()) ||
      (msg.message || '').toLowerCase().includes(msgSearch.toLowerCase())
    if (!matchesSearch) return false
    if (msgFilter === 'inbox') return isReceived
    if (msgFilter === 'sent') return !isReceived
    if (msgFilter === 'unread') return isReceived && !msg.is_read
    return true
  })

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"

  if (loading) return <LoadingSpinner label="Loading messages" />

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="cinema-page-header flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">Messages &amp; Notifications</h1>
        </div>
        <button
          onClick={() => { setReplyTo(null); setComposeData({ recipient_id: '', subject: '', message: '' }); setComposing(true) }}
          className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0"
        >
          <Send size={13} /> New Message
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/8">
        {[
          { key: 'notifications', Icon: Bell, label: 'Notifications', count: unreadNotifs },
          { key: 'messages', Icon: MessageSquare, label: 'Messages', count: unreadMsgs },
        ].map(({ key, Icon, label, count }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 font-sans text-xs transition-colors border-b-2 -mb-px ${
              tab === key
                ? 'border-cinema-glow text-cinema-glow'
                : 'border-transparent text-cinema-muted hover:text-white'
            }`}
          >
            <Icon size={14} /> {label}
            {count > 0 && (
              <span className="bg-red-500/80 text-white text-xs font-sans font-semibold rounded-full px-2 py-0.5 min-w-[20px] text-center leading-none">
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications Tab */}
      {tab === 'notifications' && (
        <div className="space-y-3">
          {unreadNotifs > 0 && (
            <div className="flex justify-end">
              <button onClick={markAllNotifsRead} className="flex items-center gap-1.5 font-sans text-xs text-cinema-glow hover:text-cinema-amber transition-colors">
                <CheckCheck size={13} /> Mark all as read
              </button>
            </div>
          )}
          {notifications.length === 0 ? (
            <div className="cinema-empty">
              <Bell size={32} className="mx-auto mb-4 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">No notifications yet</p>
            </div>
          ) : (
            <div className="cinema-list">
              {notifications.map((notif) => {
                const key = `notif-${notif.id}`
                const isOpen = expanded === key
                const Icon = TYPE_ICON[notif.type] || Info
                const colorCls = TYPE_COLOR[notif.type] || TYPE_COLOR.info
                return (
                  <div
                    key={notif.id}
                    onClick={() => toggleExpand(notif.id, 'notif')}
                    className="cinema-list-row cursor-pointer"
                  >
                    <span className={`p-1.5 border rounded-sm flex-shrink-0 ${colorCls}`}><Icon size={14} /></span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`font-sans text-sm sm:text-base ${notif.is_read ? 'text-cinema-muted' : 'text-white font-medium'}`}>{notif.title}</p>
                        <div className="flex items-center gap-2 shrink-0">
                          {!notif.is_read && <span className="w-1.5 h-1.5 bg-cinema-glow rounded-full" />}
                          <span className="font-sans text-xs text-cinema-muted">{formatDate(notif.created_at)}</span>
                          {isOpen ? <ChevronUp size={14} className="text-cinema-muted" /> : <ChevronDown size={14} className="text-cinema-muted" />}
                        </div>
                      </div>
                      {!isOpen && <p className="font-sans text-xs sm:text-sm text-cinema-muted truncate mt-1">{notif.message}</p>}
                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-white/5 font-sans text-sm sm:text-[15px] text-cinema-soft whitespace-pre-wrap leading-relaxed">{notif.message}</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Messages Tab */}
      {tab === 'messages' && (
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted" />
              <input
                type="text"
                value={msgSearch}
                onChange={e => setMsgSearch(e.target.value)}
                placeholder="Search messages..."
                className="w-full bg-white/5 border border-white/10 text-white text-xs font-sans pl-9 pr-4 py-2 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"
              />
            </div>
            <div className="flex gap-1">
              {[['all','All'],['inbox','Inbox'],['sent','Sent'],['unread','Unread']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setMsgFilter(val)}
                  className={`px-3 py-1.5 text-xs font-sans border transition-all ${
                    msgFilter === val
                      ? 'bg-cinema-glow/10 text-cinema-glow border-cinema-glow/30'
                      : 'cinema-glass border-white/8 text-cinema-muted hover:text-white'
                  }`}
                >
                  {label}
                  {val === 'unread' && unreadMsgs > 0 && (
                    <span className="ml-1 bg-red-500/80 text-white text-xs font-semibold rounded-full px-1.5 py-0.5">{unreadMsgs}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {filteredMessages.length === 0 ? (
            <div className="cinema-empty">
              <Mail size={32} className="mx-auto mb-4 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">
                {msgSearch || msgFilter !== 'all' ? 'No messages match your filter' : 'No messages yet'}
              </p>
              {!msgSearch && msgFilter === 'all' && (
                <p className="font-sans text-xs text-cinema-muted/60 mt-1">Send a message to your trainer or admin</p>
              )}
            </div>
          ) : (
            <div className="cinema-list">
              {filteredMessages.map((msg) => {
                const key = `msg-${msg.id}`
                const isOpen = expanded === key
                const isReceived = msg.recipient_id === user?.id
                const isUnread = isReceived && !msg.is_read
                const otherName = isReceived
                  ? `${msg.sender_first_name} ${msg.sender_last_name}`
                  : `${msg.recipient_first_name} ${msg.recipient_last_name}`
                return (
                  <div
                    key={msg.id}
                    onClick={() => toggleExpand(msg.id, 'msg')}
                    className="cinema-list-row cursor-pointer"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-display font-light flex-shrink-0 ${
                      isReceived ? 'bg-cinema-glow/15 text-cinema-glow' : 'bg-white/8 text-cinema-muted'
                    }`}>
                      {otherName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`font-sans text-sm sm:text-base ${isUnread ? 'text-white font-medium' : 'text-cinema-muted'}`}>
                            {isReceived ? `From: ${otherName}` : `To: ${otherName}`}
                          </p>
                          <p className={`font-sans text-xs sm:text-sm mt-0.5 ${isUnread ? 'text-cinema-soft' : 'text-cinema-muted'}`}>{msg.subject}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {isUnread && <span className="w-1.5 h-1.5 bg-cinema-glow rounded-full" />}
                          <span className="font-sans text-xs text-cinema-muted">{formatDate(msg.created_at)}</span>
                          {isOpen ? <ChevronUp size={14} className="text-cinema-muted" /> : <ChevronDown size={14} className="text-cinema-muted" />}
                        </div>
                      </div>
                      {!isOpen && <p className="font-sans text-xs sm:text-sm text-cinema-muted truncate mt-1">{msg.message}</p>}
                      {isOpen && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          <p className="font-sans text-sm sm:text-[15px] text-cinema-soft whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                          {isReceived && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleReply(msg) }}
                              className="mt-3 flex items-center gap-1.5 font-sans text-xs sm:text-sm text-cinema-glow hover:text-cinema-amber transition-colors font-medium"
                            >
                              <Reply size={13} /> Reply
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Compose / Reply Modal */}
      {composing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="cinema-glass-strong border border-white/10 rounded-sm w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/8">
              <h3 className="font-display text-lg text-white font-light">{replyTo ? 'Reply to Message' : 'New Message'}</h3>
              <button onClick={() => { setComposing(false); setReplyTo(null) }} className="text-cinema-muted hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSend} className="p-6 space-y-4">
              {!replyTo && (
                <div>
                  <label className="cinema-label block mb-2">To</label>
                  {staff.length > 0 ? (
                    <select
                      value={composeData.recipient_id}
                      onChange={e => setComposeData({ ...composeData, recipient_id: e.target.value })}
                      className={inputCls}
                      required
                    >
                      <option value="" className="bg-gray-900">Select recipient...</option>
                      {staff.map(s => (
                        <option key={s.user_id} value={s.user_id} className="bg-gray-900">
                          {s.first_name} {s.last_name} — {s.role === 'admin' ? 'Administration' : s.course_title || 'Trainer'}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" placeholder="Recipient User ID" value={composeData.recipient_id} onChange={e => setComposeData({ ...composeData, recipient_id: e.target.value })} className={inputCls} />
                  )}
                </div>
              )}
              {replyTo && (
                <div className="cinema-glass border border-white/8 rounded-sm px-4 py-2.5">
                  <p className="font-sans text-xs text-cinema-muted">Replying to: <span className="text-white">{replyTo.sender_first_name} {replyTo.sender_last_name}</span></p>
                </div>
              )}
              <div>
                <label className="cinema-label block mb-2">Subject</label>
                <input type="text" value={composeData.subject} onChange={e => setComposeData({ ...composeData, subject: e.target.value })} className={inputCls} placeholder="Subject" />
              </div>
              <div>
                <label className="cinema-label block mb-2">Message</label>
                <textarea rows={5} value={composeData.message} onChange={e => setComposeData({ ...composeData, message: e.target.value })} className={`${inputCls} resize-none`} placeholder="Type your message..." required />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={sending} className="cinema-btn-primary text-xs py-2.5 min-h-0 flex-1 flex items-center justify-center gap-2 disabled:opacity-50">
                  <Send size={13} /> {sending ? 'Sending...' : 'Send'}
                </button>
                <button type="button" onClick={() => { setComposing(false); setReplyTo(null) }} className="cinema-btn-ghost text-xs px-5 py-2.5 min-h-0">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
